// ============================================================
// SecaoRanking.js — o ranking da federação, por atleta e por ano
//
// O ranking não sai dos resultados lançados no programa: a federação
// ranqueia o estado inteiro e o programa só enxerga os próprios atletas.
// Por isso é digitado aqui.
//
// A tela existe porque a pergunta é de CONJUNTO. Na ficha, uma posição
// isolada responde "onde ele está"; aqui a lista responde "quem subiu,
// quem caiu e quem ninguém consultou", que é o que a coordenação faz com
// essa informação.
//
// E responde a última delas por ausência: atleta sem nenhuma consulta
// aparece dito, com o nome, em vez de simplesmente não estar. Na ficha a
// ausência é silêncio; numa lista ela vira tarefa.
//
// ── Posição menor é melhor ──────────────────────────────────
//
// A evolução daqui é a única conta do sistema em que o percentual
// positivo nasce de um número que CAIU. Sair de 5º para 2º é subir, e a
// tela escreve as posições ao lado do percentual justamente porque "+60%"
// sozinho não diz isso a ninguém.
// ============================================================

import { useState, useEffect, useMemo, useCallback } from "react";
import { carregarAtletas, salvarRankingDoAtleta, carregarCadastroDoNucleo } from "../firebase";
import {
  historicoComEvolucao, rotuloDoRanking, rotuloDaEvolucao, rotuloDasPosicoes,
  posicaoDoRanking, historicoDoAtleta,
} from "../rankings";
import { normalizarModalidade } from "../calculo";
import { Card, Btn, Sel, StatusBox, Carregando, Vazio, CampoDeBusca } from "../componentes";
import {
  TEXTO, CINZA_ESC, BRANCO, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO,
  AZUL, AZUL_ESCURO, OK, PERIGO,
} from "../tema";
import useIsMobile from "../useIsMobile";

// Os anos que a tela oferece: o atual e os quatro anteriores.
//
// Cinco temporadas é o que cabe numa linha sem virar rolagem, e ranking de
// mais de cinco anos atrás não entra em comparação de evolução de atleta
// em formação, que muda de categoria no caminho.
const ANOS_VISIVEIS = 5;

function anosDaTela(hoje = new Date()) {
  const atual = hoje.getFullYear();
  return Array.from({ length: ANOS_VISIVEIS }, (_, i) => atual - (ANOS_VISIVEIS - 1 - i));
}

// A cor da evolução. Verde é melhora, e melhora é o número da posição
// caindo.
function corDaEvolucao(evolucao) {
  if (!evolucao || evolucao.posicoes === 0) return CINZA_ESC;
  return evolucao.posicoes > 0 ? OK : PERIGO;
}

export default function SecaoRanking({ userNucleo }) {
  const isMobile = useIsMobile();
  const [atletas, setAtletas] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [modalidade, setModalidade] = useState("");
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [status, setStatus] = useState(null);
  const [editando, setEditando] = useState(null);   // { uid, ano }
  const [form, setForm] = useState({ estadual: "", nacional: "" });
  const [salvando, setSalvando] = useState(false);

  const anos = useMemo(() => anosDaTela(), []);

  const recarregar = useCallback(async () => {
    if (!userNucleo) return;
    setCarregando(true);
    try {
      const [lista, cadastro] = await Promise.all([
        carregarAtletas(userNucleo),
        carregarCadastroDoNucleo(userNucleo).catch(() => ({ modalidades: [] })),
      ]);
      setAtletas(lista.filter((a) => a.grupo === "ATLETA"));
      setModalidades(cadastro.modalidades || []);
    } catch (e) {
      setStatus({ tipo: "erro", msg: "Não foi possível carregar: " + (e.message || "") });
    } finally {
      setCarregando(false);
    }
  }, [userNucleo]);

  useEffect(() => { recarregar(); }, [recarregar]);

  const linhas = useMemo(() => {
    const alvo = String(busca || "").trim().toLowerCase();
    return atletas
      .filter((a) => !modalidade || normalizarModalidade(a.modalidade) === normalizarModalidade(modalidade))
      .filter((a) => !alvo || String(a.nome || "").toLowerCase().includes(alvo))
      .map((a) => {
        const historico = historicoComEvolucao(historicoDoAtleta(a));
        const porAno = new Map(historico.map((l) => [l.ano, l]));
        return { atleta: a, historico, porAno };
      })
      .sort((a, b) => {
        // Quem tem posição estadual mais recente vem primeiro, e os
        // melhor colocados na frente. Sem consulta vai para o fim, que é
        // onde a lista vira tarefa.
        const ua = a.historico[a.historico.length - 1];
        const ub = b.historico[b.historico.length - 1];
        const pa = ua ? posicaoDoRanking(ua.estadual) : null;
        const pb = ub ? posicaoDoRanking(ub.estadual) : null;
        if (pa === null && pb === null) return String(a.atleta.nome).localeCompare(String(b.atleta.nome), "pt-BR");
        if (pa === null) return 1;
        if (pb === null) return -1;
        return pa - pb;
      });
  }, [atletas, modalidade, busca]);

  const semConsulta = linhas.filter((l) => l.historico.length === 0);

  function abrirEdicao(uid, ano, linha) {
    setEditando({ uid, ano });
    setForm({
      estadual: (linha && linha.estadual) || "",
      nacional: (linha && linha.nacional) || "",
    });
    setStatus(null);
  }

  async function gravar() {
    if (!editando) return;
    setSalvando(true);
    setStatus(null);
    try {
      const r = await salvarRankingDoAtleta({
        atletaUid: editando.uid,
        ano: editando.ano,
        estadual: form.estadual.trim(),
        nacional: form.nacional.trim(),
      });
      // O servidor devolve o histórico já normalizado, e é ele que a tela
      // passa a mostrar: assim o que está na tela é o que está gravado.
      setAtletas((atuais) => atuais.map(
        (a) => (a.uid === editando.uid ? { ...a, rankings: r.rankings } : a)
      ));
      setEditando(null);
    } catch (e) {
      setStatus({ tipo: "erro", msg: e.message || "Não foi possível salvar o ranking." });
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <Carregando />;

  return (
    <div style={{ display: "grid", gap: ESP.lg }}>
      <Card>
        <div style={{ fontWeight: 800, fontSize: 18, color: TEXTO }}>Ranking da federação</div>
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 4, lineHeight: 1.6 }}>
          Uma linha por atleta, uma coluna por ano. A evolução compara o ano com o anterior
          que tiver posição: <b>posição menor é melhor</b>, então subir no ranking faz o
          percentual ficar positivo. Escreva a posição, ou <b>sem</b> quando a federação
          não ranqueia o atleta.
        </div>

        <div style={{ display: "flex", gap: ESP.md, flexWrap: "wrap", marginTop: ESP.lg, alignItems: "flex-end" }}>
          <Sel label="Modalidade" value={modalidade} onChange={setModalidade}
            options={[{ value: "", label: "Todas as modalidades" },
              ...modalidades.map((m) => ({ value: m, label: m }))]} />
          <CampoDeBusca valor={busca} onMudar={setBusca} placeholder="Procurar atleta" />
        </div>

        {/* A ausência dita, com nome. Na ficha ela é silêncio. */}
        {semConsulta.length > 0 && (
          <div style={{
            ...TIPO.apoio, color: AZUL_ESCURO, background: "#EAF0FA",
            border: "1px solid #BFD0EC", borderRadius: RAIO.md,
            padding: "10px 14px", marginTop: ESP.md, lineHeight: 1.6,
          }}>
            <b>{semConsulta.length} atleta(s) sem nenhuma consulta registrada:</b>{" "}
            {semConsulta.map((l) => l.atleta.nome).join(", ")}.
          </div>
        )}
      </Card>

      {status && <StatusBox tipo={status.tipo} msg={status.msg} />}

      <Card>
        {linhas.length === 0 ? (
          <Vazio titulo="Nenhum atleta nesta seleção." />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: isMobile ? 680 : 0 }}>
              <thead>
                <tr>
                  <th style={{ ...ESTILO_TH, textAlign: "left", minWidth: 180 }}>Atleta</th>
                  {anos.map((ano) => (
                    <th key={ano} style={{ ...ESTILO_TH, minWidth: 128 }}>{ano}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map(({ atleta, porAno }) => (
                  <tr key={atleta.uid} style={{ borderBottom: `1px solid ${BORDA}` }}>
                    <td style={{ padding: "9px 10px" }}>
                      <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 600 }}>{atleta.nome}</div>
                      <div style={{ ...TIPO.apoio, color: CINZA_ESC }}>
                        {normalizarModalidade(atleta.modalidade) || "sem modalidade"}
                        {atleta.categoria ? ` · ${atleta.categoria}` : ""}
                      </div>
                    </td>
                    {anos.map((ano) => (
                      <CelulaDoAno
                        key={ano}
                        linha={porAno.get(ano)}
                        emEdicao={Boolean(editando && editando.uid === atleta.uid && editando.ano === ano)}
                        form={form}
                        setForm={setForm}
                        salvando={salvando}
                        onAbrir={() => abrirEdicao(atleta.uid, ano, porAno.get(ano))}
                        onGravar={gravar}
                        onCancelar={() => setEditando(null)}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const ESTILO_TH = {
  fontSize: 11, fontWeight: 800, color: CINZA_ESC, textTransform: "uppercase",
  letterSpacing: ".04em", padding: "9px 10px", textAlign: "center",
  background: FUNDO_SUAVE, borderBottom: `1px solid ${BORDA}`, whiteSpace: "nowrap",
};

// A célula de um ano. Lê com um clique e edita no lugar: abrir um
// formulário em outra tela para dois números faria o lançamento de uma
// temporada inteira virar quarenta idas e vindas.
function CelulaDoAno({ linha, emEdicao, form, setForm, salvando, onAbrir, onGravar, onCancelar }) {
  if (emEdicao) {
    return (
      <td style={{ padding: "6px 8px", background: "#F8FAFC", verticalAlign: "top" }}>
        <div style={{ display: "grid", gap: 5 }}>
          <CampoCurto rotulo="Est." valor={form.estadual}
            onMudar={(v) => setForm((f) => ({ ...f, estadual: v }))} />
          <CampoCurto rotulo="Nac." valor={form.nacional}
            onMudar={(v) => setForm((f) => ({ ...f, nacional: v }))} />
          <div style={{ display: "flex", gap: 5 }}>
            <Btn tamanho="sm" onClick={onGravar} disabled={salvando}>
              {salvando ? "…" : "Salvar"}
            </Btn>
            <Btn variant="discreto" tamanho="sm" onClick={onCancelar}>Cancelar</Btn>
          </div>
        </div>
      </td>
    );
  }

  const vazia = !linha || (!linha.estadual && !linha.nacional);
  return (
    <td onClick={onAbrir} title="Clique para lançar ou corrigir"
      style={{
        padding: "9px 10px", textAlign: "center", cursor: "pointer",
        fontVariantNumeric: "tabular-nums",
      }}>
      {vazia ? (
        <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>—</span>
      ) : (
        <div style={{ display: "grid", gap: 2 }}>
          <LinhaDaPosicao rotulo="Est." valor={linha.estadual} evolucao={linha.evolucaoEstadual} />
          <LinhaDaPosicao rotulo="Nac." valor={linha.nacional} evolucao={linha.evolucaoNacional} />
        </div>
      )}
    </td>
  );
}

function LinhaDaPosicao({ rotulo, valor, evolucao }) {
  if (!valor) return null;
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "baseline", justifyContent: "center" }}>
      <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>{rotulo}</span>
      <span style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700 }}>{rotuloDoRanking(valor)}</span>
      {evolucao && (
        <span title={rotuloDasPosicoes(evolucao)}
          style={{ ...TIPO.apoio, fontWeight: 800, color: corDaEvolucao(evolucao) }}>
          {rotuloDaEvolucao(evolucao)}
        </span>
      )}
    </div>
  );
}

function CampoCurto({ rotulo, valor, onMudar }) {
  return (
    <label style={{ display: "flex", gap: 5, alignItems: "center" }}>
      <span style={{ ...TIPO.apoio, color: CINZA_ESC, width: 26 }}>{rotulo}</span>
      <input value={valor} onChange={(e) => onMudar(e.target.value)}
        placeholder="2 ou sem"
        style={{
          width: 72, border: `1px solid ${BORDA}`, borderRadius: 8, padding: "5px 7px",
          fontSize: 12.5, fontFamily: "inherit", background: BRANCO, color: AZUL,
        }} />
    </label>
  );
}
